import * as React from 'react';
import styled from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { Spinner } from '../../primitives/Spinner';
import { rem } from '../../tokens/spacing';

export interface ImportFileInputProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** DOM / automation id — sets `id` on the hidden file input. */
  semanticId: string;
  accept?: string;
  label: string;
  loading?: boolean;
  icon?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function toLegacyContainerSuffix(value: string): string {
  return value
    ?.replace(/[^a-zA-Z0-9\s]/g, '')
    ?.split(/\s+/)
    ?.map((word, index) => {
      if (index === 0) {
        return word?.toLowerCase();
      }
      return word?.charAt(0)?.toUpperCase() + word?.slice(1)?.toLowerCase();
    })
    ?.join('');
}

const StyledRoot = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

/** Full-area transparent overlay — works inside dropdown menus without FileInputLayout CSS. */
const StyledHiddenInput = styled.input`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;
`;

const StyledLabel = styled.label`
  position: relative;
  z-index: 0;
  display: block;
  width: 100%;
  box-sizing: border-box;
  cursor: pointer;
`;

const StyledLoadingRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${rem(8)};
  width: 100%;

  .spinner-container {
    flex-shrink: 0;
    width: ${rem(18)};
    height: ${rem(18)};
  }
`;

export const ImportFileInput = React.forwardRef<HTMLDivElement, ImportFileInputProps>(
  function ImportFileInput(
    {
      semanticId,
      accept = '.xlsx, .xls',
      label,
      loading = false,
      icon,
      onChange,
      className,
      onClick,
      ...rest
    },
    ref,
  ) {
    const semanticProps = getSemanticAttributes(semanticId);
    const containerClassName = [
      'input-file-container',
      'import-container',
      `${toLegacyContainerSuffix(semanticId)}Container`,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const handleContainerClick = (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      onClick?.(event);
    };

    return (
      <StyledRoot
        ref={ref}
        className={containerClassName}
        onClick={handleContainerClick}
        {...rest}
      >
        <StyledHiddenInput
          className="input-file"
          type="file"
          accept={accept}
          disabled={loading}
          onChange={onChange}
          {...semanticProps}
        />
        <StyledLabel
          className="text-center dt-btn dt-btn-outline-primary dt-btn-sm input-file-trigger sm:text-left import-label"
          htmlFor={semanticId}
        >
          {loading ? (
            <StyledLoadingRow className="flex items-center gap-2">
              <Spinner size="sm" className="spinner-container" />
              {label}
            </StyledLoadingRow>
          ) : (
            <>
              {icon ? <span className="material-symbols-outlined">{icon}</span> : null} {label}
            </>
          )}
        </StyledLabel>
      </StyledRoot>
    );
  },
);

ImportFileInput.displayName = 'ImportFileInput';
